import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import type { DnsRecordsQuery } from "@/features/dns/services/dnsService";
import {
  createDnsRecord,
  deleteDnsRecord,
  fetchConfiguredDnsZone,
  fetchDnsRecords,
  fetchDnsRecordsDefaultZone,
  fetchDnsZones,
  updateDnsRecord,
} from "@/features/dns/services/dnsService";

export const dnsQueryKeys = {
  configuredZone: ["dns", "configuredZone"] as const,
  zones: ["dns", "zones"] as const,
  records: (scope: "default" | string, query: DnsRecordsQuery) =>
    ["dns", "records", scope, query] as const,
};

export function useConfiguredDnsZone() {
  return useQuery({
    queryKey: dnsQueryKeys.configuredZone,
    queryFn: fetchConfiguredDnsZone,
    staleTime: 60_000,
  });
}

export function useDnsZonesList(enabled: boolean) {
  return useQuery({
    queryKey: dnsQueryKeys.zones,
    queryFn: fetchDnsZones,
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export function useDnsRecords(
  defaultZoneId: string | undefined,
  selectedZoneId: string | undefined,
  query: DnsRecordsQuery,
  enabled: boolean
) {
  const useDefaultPath =
    Boolean(defaultZoneId && selectedZoneId && selectedZoneId === defaultZoneId);

  return useQuery({
    queryKey: dnsQueryKeys.records(useDefaultPath ? "default" : (selectedZoneId ?? ""), query),
    queryFn: () =>
      useDefaultPath
        ? fetchDnsRecordsDefaultZone(query)
        : fetchDnsRecords(selectedZoneId as string, query),
    enabled: enabled && Boolean(selectedZoneId),
  });
}

/** `null` en zona significa usar rutas sin `:zoneId` (zona por defecto del servidor). */
export function useDnsRecordMutations(zoneIdForApi: string | null) {
  const qc = useQueryClient();

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["dns", "records"] });
  };

  const createMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => createDnsRecord(zoneIdForApi, body),
    onSuccess: () => {
      invalidate();
      toast.success("Registro DNS creado");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateMut = useMutation({
    mutationFn: ({ recordId, body }: { recordId: string; body: Record<string, unknown> }) =>
      updateDnsRecord(zoneIdForApi, recordId, body),
    onSuccess: () => {
      invalidate();
      toast.success("Registro actualizado");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (recordId: string) => deleteDnsRecord(zoneIdForApi, recordId),
    onSuccess: () => {
      invalidate();
      toast.success("Registro eliminado");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return { createMut, updateMut, deleteMut };
}
